import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Flame, Sun, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { Sparkles, Flame, Sun };

/**
 * HerzLevel — own gamification track for the Herzbewegung.
 * Distinct from CompassionScore (which counts SAT + Herz combined).
 * This widget shows: current level (Funke/Flamme/Feuer/Leuchtfeuer/Sonne),
 * progress to the next level, and the full ladder so users see where they're going.
 */
export default function HerzLevel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("sat_token");
    if (!token) { setLoading(false); return; }
    axios
      .get(`${API}/api/herz/me/level`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="animate-spin text-[#D4AF37]" size={18} />
      </div>
    );
  }
  if (!data) return null;

  const Icon = ICONS[data.level.icon] || Sparkles;
  const progressPct = Math.round(data.progress_to_next * 100);

  return (
    <div className="rounded-3xl border-2 p-6 md:p-8 mb-10"
         style={{ borderColor: data.level.color + "55", background: `radial-gradient(ellipse at top, ${data.level.color}10 0%, rgba(26,12,18,0.6) 70%)` }}
         data-testid="herz-level-widget">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-2 inline-flex items-center gap-2">
            <Icon size={12} /> Dein Herzlevel
          </div>
          <div className="serif font-light leading-tight" style={{ fontSize: "2.5rem", color: data.level.color }} data-testid="herz-level-label">
            {data.level.label}
          </div>
          <p className="text-[#F7EEDD]/70 italic text-sm mt-2 max-w-md leading-relaxed">
            {data.level.story}
          </p>
        </div>
        <div className="text-right">
          <div className="serif text-5xl font-light" style={{ color: data.level.color }} data-testid="herz-level-count">
            {data.acts_count}
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F7EEDD]/55 mt-1">
            Herzenstaten
          </div>
        </div>
      </div>

      {/* Progress bar */}
      {data.next_level ? (
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-[#F7EEDD]/70 mb-2">
            <span>Auf dem Weg zur Stufe <b style={{ color: data.next_level.color }}>{data.next_level.label}</b></span>
            <span>Noch <b style={{ color: data.level.color }}>{data.acts_to_next}</b> {data.acts_to_next === 1 ? "Tat" : "Taten"}</span>
          </div>
          <div className="h-2.5 bg-[#F7EEDD]/8 rounded-full overflow-hidden">
            <div className="h-full transition-all duration-700 rounded-full"
                 style={{ width: `${progressPct}%`, background: `linear-gradient(90deg, ${data.level.color} 0%, ${data.next_level.color} 100%)` }}
                 data-testid="herz-level-progress" />
          </div>
        </div>
      ) : (
        <div className="mb-6 text-center text-sm text-[#F7EEDD]/80 italic">
          ★ Du hast die höchste Stufe erreicht. Du bist die Sonne. ★
        </div>
      )}

      {/* Ladder strip */}
      <div className="grid grid-cols-5 gap-2" data-testid="herz-level-ladder">
        {data.all_levels.map((lvl) => {
          const Lico = ICONS[lvl.icon] || Sparkles;
          const reached = data.acts_count >= lvl.min;
          const current = lvl.key === data.level.key;
          return (
            <div key={lvl.key}
                 className={`text-center rounded-xl p-3 border transition ${current ? "scale-105" : ""}`}
                 style={{
                   borderColor: reached ? lvl.color : "rgba(247,238,221,0.08)",
                   background: current ? `${lvl.color}15` : reached ? "rgba(247,238,221,0.03)" : "transparent",
                   opacity: reached ? 1 : 0.4,
                 }}
                 data-testid={`herz-rung-${lvl.key}`}>
              <Lico size={16} className="mx-auto" style={{ color: reached ? lvl.color : "#F7EEDD55" }} />
              <div className="text-[11px] tracking-wider uppercase mt-1.5" style={{ color: reached ? lvl.color : "#F7EEDD55" }}>
                {lvl.label}
              </div>
              <div className="text-[9px] mt-0.5" style={{ color: "#F7EEDD55" }}>
                {lvl.max ? `${lvl.min}–${lvl.max}` : `${lvl.min}+`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
